import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { FileText, Upload as UploadIcon, Shield, TrendingUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { FREE_TIER_BYTES, formatBytes, getQuotaPercentage } from '../lib/quota';

const DashboardPage = () => {
  const { user, profile } = useAuth();
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [user, profile]);

  const fetchData = async () => {
    if (!user) return;
    try {
      const { data: filesData } = await supabase
        .from('files')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      setFiles(filesData || []);

      if (profile?.role === 'admin') {
        const { count: userCount } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });
        const { count: fileCount } = await supabase
          .from('files')
          .select('*', { count: 'exact', head: true });
        const { data: allFiles } = await supabase
          .from('files')
          .select('file_size');
        const totalStorage = (allFiles || []).reduce((sum, f) => sum + (f.file_size || 0), 0);

        setStats({ total_users: userCount || 0, total_files: fileCount || 0, total_storage: totalStorage });
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalSize = files.reduce((sum, file) => sum + (file.file_size || 0), 0);
  const quotaPercentage = getQuotaPercentage(totalSize);
  const quotaLimitReached = totalSize >= FREE_TIER_BYTES;
  const quotaBarColor = quotaPercentage > 90
    ? 'bg-destructive'
    : quotaPercentage >= 75
      ? 'bg-[hsl(var(--warning))]'
      : 'bg-[hsl(var(--success))]';

  const renderStatSkeletons = () => (
    <>
      {Array.from({ length: 2 }).map((_, index) => (
        <div key={index} className="bg-card border border-border rounded-lg p-6 animate-pulse" data-testid="stat-skeleton-card">
          <div className="w-8 h-8 rounded bg-muted mb-3" />
          <div className="w-16 h-7 rounded bg-muted mb-2" />
          <div className="w-24 h-4 rounded bg-muted" />
        </div>
      ))}
    </>
  );

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="dashboard-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-base text-muted-foreground mt-1">Welcome back, {profile?.name || 'User'}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? renderStatSkeletons() : (
            <>
          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-files">
            <FileText className="w-8 h-8 text-primary mb-2" />
            <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{files.length}</p>
            <p className="text-sm text-muted-foreground">Your Files</p>
          </div>

          <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-storage">
            <Shield className="w-8 h-8 text-[hsl(var(--success))] mb-2" />
            <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{formatBytes(totalSize)}</p>
            <p className="text-sm text-muted-foreground">Total Storage</p>
          </div>

          {profile?.role === 'admin' && stats && (
            <>
              <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-total-users">
                <TrendingUp className="w-8 h-8 text-[hsl(var(--warning))] mb-2" />
                <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.total_users}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
              <div className="bg-card border border-border rounded-lg p-6" data-testid="stat-total-files">
                <FileText className="w-8 h-8 text-primary mb-2" />
                <p className="text-2xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{stats.total_files}</p>
                <p className="text-sm text-muted-foreground">All Files</p>
              </div>
            </>
          )}
            </>
          )}
        </div>

        {/* Storage Quota */}
        <div className="bg-card border border-border rounded-lg p-6" data-testid="storage-quota">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
            <h2 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Storage Quota
            </h2>
            <p className="text-sm text-muted-foreground font-mono">
              Storage Used: {formatBytes(totalSize)} of {formatBytes(FREE_TIER_BYTES)} ({quotaPercentage}%)
            </p>
          </div>
          <div className="h-3 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${quotaBarColor}`}
              style={{ width: `${quotaPercentage}%` }}
              data-testid="storage-quota-bar"
            />
          </div>
          {quotaPercentage > 90 && !quotaLimitReached && (
            <p className="text-sm text-[hsl(var(--warning))] mt-3">You are approaching your storage limit</p>
          )}
          {quotaLimitReached && (
            <p className="text-sm text-destructive mt-3">Storage limit reached. Delete files to upload more.</p>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Quick Actions</h2>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/upload" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto" data-testid="quick-upload-btn">
                <UploadIcon className="w-4 h-4 mr-2" />Upload File
              </Button>
            </Link>
            <Link to="/files" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="quick-files-btn">
                <FileText className="w-4 h-4 mr-2" />View All Files
              </Button>
            </Link>
          </div>
        </div>

        {/* Recent Files */}
        {files.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <h2 className="text-xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Recent Files</h2>
            <div className="space-y-3">
              {files.slice(0, 5).map((file) => (
                <div key={file.id} className="flex items-center justify-between py-2 border-b border-border last:border-0" data-testid="recent-file-item">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{file.file_name}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {formatBytes(file.file_size)} &middot; {new Date(file.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {file.encryption_iv && (
                    <span className="text-xs px-2 py-0.5 rounded bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]">Encrypted</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
